import { cacheUtils, questionsUtils } from "../../..";
import { api, state, temp, ui } from "../../../interfaces/interfaces";
import AddressUtils from "@utils/address";
import { CacheTypes } from "@utils/cache/interfaces";
import {
  filterGroupsMatchingTrade,
  filterJobsMatchingGroupByDesc,
  filterTradesMatchingTradeCode,
  filterGroupMatchingGroupCode,
  filterJobsMatchingGroup,
  filterJobTypesMatchingJobTypeCode,
} from "@utils/general";
import { jobTypesWithRequirements } from "@utils/requiredOptions/interfaces";
import StorageUtils from "@services/storage";
import subscriptions from "@services/subscriptions/instance";
import { dispatchSetMultipleState } from "@reducers/stateReducer";
import ObjectsOperations from "@utils/objects/operations";

export const stage1Middleware = async (action: { payload: { name: string; value: any } }, next: any) => {
  switch (action.payload.name) {
    case "ProblemComboTrade":
      if (action.payload.value === null) {
        ui.ProblemCategories = [];
        ui.ProblemComboCategory = null;
        state.JobTradeCode = "";
      } else {
        if (action.payload.value?.fuzzySearch) {
          action.payload.value.fuzzySearch = undefined;
        } else {
          ui.ProblemComboCategory = null;
        }
        const tradeCode = action.payload.value.tradeCode;
        const tradeGroupsMap = cacheUtils.getCache<CacheTypes.TradeGroupsMap>("TradeGroupsMap");
        const activeGroups = cacheUtils.getCache<CacheTypes.ActiveGroups>("ActiveGroups");
        const jobTypes = cacheUtils.getCache<CacheTypes.JobTypes>("JobTypes");

        const problemCategories = filterGroupsMatchingTrade(tradeCode, tradeGroupsMap, activeGroups, jobTypes);
        ui.ProblemCategories = problemCategories;

        state.JobTradeCode = action.payload.value.tradeCode;

        if (ui.ProblemAddressString) {
          const token = AddressUtils.cancellationTokens.generateToken();
          await AddressUtils.checkCoverage({
            ui,
            token,
            api,
            callbackFn: (value: boolean) => {
              temp.ProblemAddressCoverage = value;
            },
          });
        }

        // const token = AddressUtils.generateCancellationTokenKey();
        // const result = await AddressUtils.checkCoverage({ cache, ui, globals, job, token });
      }
      return next(action);

    case "ProblemComboCategory":
      if (action.payload.value === null) {
        ui.ProblemJobs = [];
        ui.ProblemComboJob = null;
      }
      if (action.payload.value?.groupDescription && ui.ProblemComboTrade) {
        const problemJobs = filterJobsMatchingGroupByDesc(
          ui.ProblemComboTrade.tradeCode,
          action.payload.value.groupDescription,
          cacheUtils.getCache("JobTypes"),
          cacheUtils.getCache("GroupTypes")
        );
        ui.ProblemJobs = problemJobs;
        ui.ProblemComboJob = null;
      }
      return next(action);

    case "ProblemInputPostcode":
      if (action.payload.value && ui.ProblemInputPostcode !== action.payload.value) {
        const valid = AddressUtils.validatePostcode(action.payload.value);
        // if valid set search icon to enabled
        if (valid) {
          action.payload.value = AddressUtils.formatPostcode(action.payload.value);
          state.JobPostcode = action.payload.value;
          temp.ProblemAddressActiveSearch = true;
          AddressUtils.getPostcodeValues({ postcode: action.payload.value });
        } else {
          temp.ProblemAddressActiveSearch = false;
        }
        // clear all address fields
        AddressUtils.clearPropertyAddressForm();
        // mark address search as not complete
        temp.ProblemAddressSearchDone = false;
        // mark coverage as not complete
        temp.ProblemAddressCoverage = false;
        // clear previous address search results
        StorageUtils.deleteValuesFromStoreInIndexedDB({ tableName: "PropertyAddresses" });
        // set addresses length to 0
        temp.PropertyAddressAddressesLength = 0;
        // clear previous address string
        ui.ProblemAddressString = "";
        // clear previous selected address
        ui.PropertyAddressSelectedAddress = undefined;
        // clear last job
        state.LastJob = [];
        // clear caravan site flag
        state.PossibleCaravanSite = false;
      }
      return next(action);

    case "ProblemComboJob":
      if (action.payload.value?.tradeCode) {
        dispatchSetMultipleState({
          JobTypeCode: action.payload.value.jobTypeCode,
          JobIsQuote: action.payload.value.isQuoteJobType,
          JobTradeCode: action.payload.value.tradeCode,
        });

        state.IsAQuote = action.payload.value.isQuoteJobType;

        questionsUtils.loadQuestions(action.payload.value.jobTypeCode);

        const requiredOptions = jobTypesWithRequirements.includes(action.payload.value.jobTypeCode);
        temp.RequiredOptions = requiredOptions;

        subscriptions.publish("JobTypeCode", action.payload.value.jobTypeCode);

        state.ProblemComboJobValid = true;
      } else {
        state.ProblemComboJobValid = false;
        temp.RequiredOptions = false;
      }
      state.RequiredOptionsValid = false;

      return next(action);

    case "ProblemAddressString":
      if (action.payload.value) {
        const token = AddressUtils.cancellationTokens.generateToken();
        await AddressUtils.checkCoverage({
          ui,
          token,
          api,
          callbackFn: (value: boolean) => {
            temp.ProblemAddressCoverage = value;
          },
        });
      }
      return next(action);

    case "ProblemComboJobSearch":
      if (action.payload.value?.jobTypeCode) {
        const tradeCode = action.payload.value.tradeCode;
        const comboTrade = {
          ...filterTradesMatchingTradeCode(tradeCode, cacheUtils.getCache<CacheTypes.TradeTypes>("TradeTypes")),
        };

        comboTrade && (comboTrade.fuzzySearch = true);
        if (JSON.stringify(ui.ProblemComboTrade) !== JSON.stringify(comboTrade)) {
          ui.ProblemComboTrade = ObjectsOperations.typeSafeStructuredClone(comboTrade);
        }

        const problemCategories = filterGroupsMatchingTrade(
          tradeCode,
          cacheUtils.getCache("TradeGroupsMap"),
          cacheUtils.getCache("ActiveGroups"),
          cacheUtils.getCache("JobTypes")
        );
        if (JSON.stringify(ui.ProblemCategories) !== JSON.stringify(problemCategories)) {
          ui.ProblemCategories = problemCategories;
        }

        ui.ProblemJobs = [];

        const { jobGroupCode: groupCode, groupDescription } = filterGroupMatchingGroupCode(
          action.payload.value?.jobGroupCode,
          cacheUtils.getCache("GroupTypes")
        );

        const problemComboCategory = { groupCode, groupDescription, fuzzySearch: true };
        ui.ProblemComboCategory = problemComboCategory;

        const problemJobs = filterJobsMatchingGroup(
          tradeCode,
          action.payload.value?.jobGroupCode,
          cacheUtils.getCache("JobTypes")
        );
        ui.ProblemJobs = problemJobs;

        const comboJob = { ...filterJobTypesMatchingJobTypeCode(problemJobs, action.payload.value?.jobTypeCode) };
        comboJob && (comboJob.fuzzySearch = true);
        ui.ProblemComboJob = ObjectsOperations.typeSafeStructuredClone(comboJob);

        // job.tradeCode = tradeCode;
        // call.tradeCode = tradeCode;
        // job.jobTypeCode = action.payload.value?.jobTypeCode;

        ui.RateText = "";
      } else {
        ui.ProblemComboTrade = null;
        ui.ProblemComboJob = null;
        ui.ProblemComboCategory = null;
      }
      return next(action);

    default:
      return next(action);
  }
};
